import React, { useRef, useImperativeHandle, useState, useContext } from 'react';
import * as Headless from '@headlessui/react';
import { cn } from './cn';
import { FieldIdContext } from './fieldset';

export type FileInputRef = {
  clearFiles: () => void;
  input: HTMLInputElement | null;
  files: File[];
};

export type FileInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  invalid?: boolean;
};

const FileInput = React.forwardRef<FileInputRef, FileInputProps>(
  ({ className, invalid, onChange, id: propId, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const fieldId = useContext(FieldIdContext);

    useImperativeHandle(ref, () => ({
      clearFiles: () => {
        if (inputRef.current) {
          inputRef.current.value = '';
        }
        setFileName(null);
      },
      get input() {
        return inputRef.current;
      },
      get files() {
        return inputRef.current?.files ? Array.from(inputRef.current.files) : [];
      }
    }));

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      setFileName(file?.name || null);
      onChange?.(e);
    };

    return (
      <div data-slot="control" className="relative">
        <Headless.Input
          ref={inputRef}
          type="file"
          id={propId ?? fieldId}
          onChange={handleChange}
          invalid={invalid}
          className={cn(
            'block w-full text-sm text-zinc-500 dark:text-zinc-400',
            'file:mr-4 file:py-2 file:px-4',
            'file:rounded-lg file:border-0',
            'file:text-sm file:font-medium',
            'file:bg-primary-50 file:text-primary-700',
            'hover:file:bg-primary-100',
            'dark:file:bg-zinc-700 dark:file:text-zinc-200',
            'dark:hover:file:bg-zinc-600',
            invalid && 'border border-danger-500 rounded-lg',
            className
          )}
          {...props}
        />
        {fileName && <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{fileName}</p>}
      </div>
    );
  }
);

FileInput.displayName = 'FileInput';

export default FileInput;
